/**
 * RichDocument — renders an Outline / ProseMirror document JSON
 * (`{ type: "doc", content: [...] }`) as a polished, readable, Fluent-native
 * document. This is the canonical renderer for Knowledge Base article bodies
 * and any other PM-shaped content; never hand-roll markdown rendering again.
 *
 * Reuses toolkit primitives: `MonoText` (inline code), `CodeBlock` (fenced
 * blocks), `Chip` (internal-link + mention chips). All sizing is rem/em
 * relative and driven from a single `fontScale` so a parent A-/A+ control
 * scales headings, body, and spacing proportionally. Tokens only — no hex,
 * no inline Cascadia Code rolls.
 */
import { useCallback, useState, type CSSProperties, type ReactNode } from 'react';
import {
  Checkbox,
  Divider,
  Link,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { DocumentRegular, DismissRegular } from '@fluentui/react-icons';
import { MonoText } from './MonoText';
import { CodeBlock } from './CodeBlock';
import { Chip } from './Chip';
import { richHeadingSlug } from './richHeadingSlug';
import { useLocale } from '../i18n/useLocale';

/* ------------------------------------------------------------------ */
/* Types — permissive on purpose so an unexpected shape never crashes. */
/* ------------------------------------------------------------------ */

export interface PMMark {
  type: string;
  attrs?: Record<string, unknown> & { href?: string };
}

export interface ProseMirrorNode {
  type: string;
  content?: ProseMirrorNode[];
  text?: string;
  marks?: PMMark[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attrs?: any;
}

export interface RichDocumentProps {
  /** The `content_json` of the article (a `doc` node or any PM node). */
  doc: ProseMirrorNode | null | undefined;
  /** Called with the parsed article id when an internal `kb://article/<id>` link is clicked. */
  onInternalLink?: (articleId: number) => void;
  /** Scales the whole document (1 = base). Wire a parent A- / A+ control here. */
  fontScale?: number;
}

/* ------------------------------------------------------------------ */
/* Link helpers                                                       */
/* ------------------------------------------------------------------ */

const KB_PREFIX = 'kb://article/';

/** Returns the numeric article id if href is an internal KB link, else null. */
function parseInternalArticleId(href: string | undefined): number | null {
  if (!href || !href.startsWith(KB_PREFIX)) return null;
  const raw = href.slice(KB_PREFIX.length).split(/[/?#]/)[0];
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
}

/* ------------------------------------------------------------------ */
/* Styles — all rem/em relative; the root `fontSize` drives the ramp. */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles({
  root: {
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.65',
    fontSize: '1rem',
    // Comfortable, document-like body font (sans), not the app chrome size.
    fontFamily: tokens.fontFamilyBase,
    wordBreak: 'break-word',
  },
  // Constrain prose to a readable measure; tables/images opt out below.
  prose: {
    maxWidth: '72ch',
  },
  paragraph: {
    margin: '0 0 1em 0',
  },
  // Headings — clear hierarchy, generous top margin, tight to following text.
  h1: {
    fontSize: '1.9em',
    fontWeight: tokens.fontWeightBold,
    lineHeight: '1.2',
    margin: '1.6em 0 0.5em 0',
    color: tokens.colorNeutralForeground1,
  },
  h2: {
    fontSize: '1.45em',
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1.25',
    margin: '1.4em 0 0.45em 0',
    color: tokens.colorNeutralForeground1,
    paddingBottom: '0.2em',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  h3: {
    fontSize: '1.18em',
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1.3',
    margin: '1.2em 0 0.35em 0',
    color: tokens.colorNeutralForeground1,
  },
  // First block should not push down from the top.
  firstBlock: {
    marginTop: 0,
  },
  // Inline marks
  highlight: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorNeutralForeground1,
    borderRadius: tokens.borderRadiusSmall,
    padding: '0.05em 0.25em',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
  },
  inlineCode: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    padding: '0.08em 0.35em',
  },
  // Lists
  list: {
    margin: '0 0 1em 0',
    paddingLeft: '1.6em',
  },
  listItem: {
    margin: '0.25em 0',
    lineHeight: '1.6',
  },
  // Read-only checkbox list
  checkboxList: {
    listStyle: 'none',
    margin: '0 0 1em 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalXS,
  },
  checkboxControl: {
    marginTop: '0.05em',
  },
  checkboxContent: {
    flex: 1,
    minWidth: 0,
    lineHeight: '1.6',
  },
  // Divider
  divider: {
    margin: '1.6em 0',
  },
  // Tables — bordered, header bold, horizontally scrollable on narrow widths.
  tableScroll: {
    width: '100%',
    overflowX: 'auto',
    margin: '0 0 1.2em 0',
  },
  table: {
    borderCollapse: 'collapse',
    width: 'auto',
    minWidth: '100%',
    fontSize: '0.95em',
  },
  th: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'left',
    padding: '0.5em 0.75em',
    verticalAlign: 'top',
  },
  td: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground1,
    padding: '0.5em 0.75em',
    verticalAlign: 'top',
  },
  // Images
  image: {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'zoom-in',
    display: 'block',
    margin: '0.6em 0 1.2em 0',
  },
  // Lightbox overlay
  lightboxOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 100000,
    backgroundColor: tokens.colorBackgroundOverlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalXL,
    cursor: 'zoom-out',
  },
  lightboxImage: {
    maxWidth: '95vw',
    maxHeight: '92vh',
    objectFit: 'contain',
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow64,
  },
  lightboxClose: {
    position: 'fixed',
    top: tokens.spacingVerticalL,
    right: tokens.spacingHorizontalL,
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackgroundStatic,
    color: tokens.colorNeutralForegroundStaticInverted,
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.25em',
  },
  // Internal-link chip — inline, reads as a link
  inlineDocChip: {
    display: 'inline-flex',
    verticalAlign: 'baseline',
    margin: '0 0.1em',
  },
  mentionChip: {
    display: 'inline-flex',
    verticalAlign: 'baseline',
    margin: '0 0.1em',
  },
  break: {
    display: 'block',
    content: '""',
  },
});

/* ------------------------------------------------------------------ */
/* Renderer                                                           */
/* ------------------------------------------------------------------ */

interface RenderCtx {
  styles: ReturnType<typeof useStyles>;
  onInternalLink?: (articleId: number) => void;
  openLightbox: (src: string, alt?: string) => void;
  /** Maps each heading node → its document-order ordinal, for stable anchor ids. */
  headingIndex: WeakMap<ProseMirrorNode, number>;
}

let keySeq = 0;
function nextKey(prefix: string): string {
  keySeq += 1;
  return `${prefix}-${keySeq}`;
}

/**
 * Pre-pass: assign every heading node a document-order ordinal (0-based). Built
 * once per doc (pure, no render-time mutation) so anchor ids line up with a
 * consumer's `richHeadingSlug(text, index)` TOC walk.
 */
function buildHeadingIndex(doc: ProseMirrorNode): WeakMap<ProseMirrorNode, number> {
  const map = new WeakMap<ProseMirrorNode, number>();
  let n = 0;
  const walk = (node: ProseMirrorNode) => {
    if (node.type === 'heading') {
      map.set(node, n);
      n += 1;
    }
    for (const child of node.content ?? []) walk(child);
  };
  walk(doc);
  return map;
}

/** Render an array of child nodes. */
function renderChildren(nodes: ProseMirrorNode[] | undefined, ctx: RenderCtx): ReactNode {
  if (!nodes || nodes.length === 0) return null;
  return nodes.map((child) => <RenderNode key={nextKey(child.type)} node={child} ctx={ctx} />);
}

/** Apply text marks (strong/em/highlight/code/link) around a text leaf. */
function renderText(node: ProseMirrorNode, ctx: RenderCtx): ReactNode {
  const { styles } = ctx;
  const text = node.text ?? '';
  const marks = node.marks ?? [];

  // Inline code wins on typography — render with the toolkit mono primitive.
  const hasCode = marks.some((m) => m.type === 'code');
  let content: ReactNode = hasCode ? (
    <MonoText size="medium" className={styles.inlineCode}>
      {text}
    </MonoText>
  ) : (
    text
  );

  // Wrap with formatting marks (innermost → outermost).
  for (const mark of marks) {
    switch (mark.type) {
      case 'strong':
        content = <strong key={nextKey('strong')}>{content}</strong>;
        break;
      case 'em':
        content = <em key={nextKey('em')}>{content}</em>;
        break;
      case 'highlight':
        content = (
          <mark key={nextKey('hl')} className={styles.highlight}>
            {content}
          </mark>
        );
        break;
      case 'link': {
        const href = mark.attrs?.href as string | undefined;
        const internalId = parseInternalArticleId(href);
        if (internalId !== null) {
          content = (
            <Link
              key={nextKey('ilink')}
              href={href}
              onClick={(e) => {
                e.preventDefault();
                ctx.onInternalLink?.(internalId);
              }}
            >
              {content}
            </Link>
          );
        } else {
          content = (
            <Link
              key={nextKey('elink')}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {content}
            </Link>
          );
        }
        break;
      }
      default:
        break;
    }
  }

  return content;
}

interface RenderNodeProps {
  node: ProseMirrorNode;
  ctx: RenderCtx;
  /** True if this is the first block at the top level (kill top margin). */
  isFirst?: boolean;
}

function RenderNode({ node, ctx, isFirst }: RenderNodeProps): ReactNode {
  const { styles } = ctx;
  const attrs = node.attrs ?? {};

  switch (node.type) {
    case 'doc':
      return <>{renderChildren(node.content, ctx)}</>;

    case 'text':
      return <>{renderText(node, ctx)}</>;

    case 'paragraph':
      return (
        <p className={mergeClasses(styles.paragraph, isFirst && styles.firstBlock)}>
          {renderChildren(node.content, ctx)}
        </p>
      );

    case 'heading': {
      const level = Math.min(Math.max(Number(attrs.level) || 1, 1), 3);
      const cls = level === 1 ? styles.h1 : level === 2 ? styles.h2 : styles.h3;
      const Tag = (`h${level}` as 'h1' | 'h2' | 'h3');
      // Stable anchor id (ordinal among headings) so a parent TOC can scroll to it.
      const slug = richHeadingSlug(collectText(node), ctx.headingIndex.get(node) ?? 0);
      return (
        <Tag id={slug} className={mergeClasses(cls, isFirst && styles.firstBlock)}>
          {renderChildren(node.content, ctx)}
        </Tag>
      );
    }

    case 'br':
    case 'hard_break':
    case 'hardBreak':
      return <br />;

    case 'hr':
    case 'horizontal_rule':
    case 'horizontalRule':
      return <Divider className={styles.divider} />;

    case 'bullet_list':
    case 'bulletList':
      return <ul className={styles.list}>{renderChildren(node.content, ctx)}</ul>;

    case 'ordered_list':
    case 'orderedList': {
      const start = Number(attrs.start);
      return (
        <ol className={styles.list} start={Number.isFinite(start) ? start : undefined}>
          {renderChildren(node.content, ctx)}
        </ol>
      );
    }

    case 'list_item':
    case 'listItem':
      return <li className={styles.listItem}>{renderChildren(node.content, ctx)}</li>;

    case 'checkbox_list':
    case 'checkboxList':
    case 'todo_list':
    case 'taskList':
      return <ul className={styles.checkboxList}>{renderChildren(node.content, ctx)}</ul>;

    case 'checkbox_item':
    case 'checkboxItem':
    case 'todo_item':
    case 'taskItem': {
      const checked = Boolean(attrs.checked);
      return (
        <li className={styles.checkboxItem}>
          <Checkbox
            className={styles.checkboxControl}
            checked={checked}
            disabled
            // Read-only display — never editable in a rendered article.
            aria-readonly
          />
          <div className={styles.checkboxContent}>{renderChildren(node.content, ctx)}</div>
        </li>
      );
    }

    case 'table':
      return (
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <tbody>{renderChildren(node.content, ctx)}</tbody>
          </table>
        </div>
      );

    case 'tr':
    case 'table_row':
    case 'tableRow':
      return <tr>{renderChildren(node.content, ctx)}</tr>;

    case 'th':
    case 'table_header':
    case 'tableHeader': {
      const colspan = Number(attrs.colspan);
      const rowspan = Number(attrs.rowspan);
      return (
        <th
          className={styles.th}
          colSpan={Number.isFinite(colspan) && colspan > 1 ? colspan : undefined}
          rowSpan={Number.isFinite(rowspan) && rowspan > 1 ? rowspan : undefined}
        >
          {renderChildren(node.content, ctx)}
        </th>
      );
    }

    case 'td':
    case 'table_cell':
    case 'tableCell': {
      const colspan = Number(attrs.colspan);
      const rowspan = Number(attrs.rowspan);
      return (
        <td
          className={styles.td}
          colSpan={Number.isFinite(colspan) && colspan > 1 ? colspan : undefined}
          rowSpan={Number.isFinite(rowspan) && rowspan > 1 ? rowspan : undefined}
        >
          {renderChildren(node.content, ctx)}
        </td>
      );
    }

    case 'image': {
      const src = attrs.src as string | undefined;
      if (!src) return null;
      const alt = (attrs.alt as string | undefined) ?? '';
      const w = attrs.width;
      const h = attrs.height;
      const sizeStyle: CSSProperties = {};
      if (w != null && w !== '') sizeStyle.width = typeof w === 'number' ? `${w}px` : String(w);
      if (h != null && h !== '') sizeStyle.height = typeof h === 'number' ? `${h}px` : String(h);
      return (
        <img
          className={styles.image}
          src={src}
          alt={alt}
          loading="lazy"
          style={sizeStyle}
          onClick={() => ctx.openLightbox(src, alt)}
        />
      );
    }

    case 'code_block':
    case 'codeBlock': {
      const raw = collectText(node);
      return <CodeBlock copyValue={raw} wrap>{raw}</CodeBlock>;
    }

    case 'document': {
      // Inline internal/external document link chip.
      const href = attrs.href as string | undefined;
      const label =
        (attrs.label as string | undefined) ||
        collectText(node) ||
        (attrs.modelId ? String(attrs.modelId) : 'Document');
      const internalId = parseInternalArticleId(href);
      return (
        <span className={styles.inlineDocChip}>
          <Chip
            color="brand"
            icon={<DocumentRegular />}
            onClick={() => {
              if (internalId !== null) {
                ctx.onInternalLink?.(internalId);
              } else if (href) {
                window.open(href, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            {label}
          </Chip>
        </span>
      );
    }

    case 'mention': {
      const label = (attrs.label as string | undefined) ?? (attrs.id ? String(attrs.id) : '');
      return (
        <span className={styles.mentionChip}>
          <Chip color="informative">@{label}</Chip>
        </span>
      );
    }

    default:
      // Unknown node — degrade gracefully by rendering children.
      return <>{renderChildren(node.content, ctx)}</>;
  }
}

/** Flatten all descendant text leaves into a single string. */
function collectText(node: ProseMirrorNode): string {
  if (node.type === 'text') return node.text ?? '';
  if (!node.content) return '';
  return node.content.map(collectText).join('');
}

/* ------------------------------------------------------------------ */
/* Public component                                                   */
/* ------------------------------------------------------------------ */

export function RichDocument({ doc, onInternalLink, fontScale = 1 }: RichDocumentProps) {
  const styles = useStyles();
  const { t } = useLocale();
  const [lightbox, setLightbox] = useState<{ src: string; alt?: string } | null>(null);

  const openLightbox = useCallback((src: string, alt?: string) => {
    setLightbox({ src, alt });
  }, []);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  if (!doc || !doc.content || doc.content.length === 0) return null;

  const ctx: RenderCtx = {
    styles,
    onInternalLink,
    openLightbox,
    headingIndex: buildHeadingIndex(doc),
  };

  return (
    <>
      <div
        className={mergeClasses(styles.root, styles.prose)}
        style={{ fontSize: `calc(1rem * ${fontScale})` }}
      >
        {doc.content.map((child, i) => (
          <RenderNode key={nextKey(child.type)} node={child} ctx={ctx} isFirst={i === 0} />
        ))}
      </div>

      {lightbox ? (
        <div
          className={styles.lightboxOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={lightbox.alt || t('common.image')}
          onClick={closeLightbox}
        >
          <button type="button" className={styles.lightboxClose} onClick={closeLightbox} aria-label={t('common.close')}>
            <DismissRegular />
          </button>
          <img
            className={styles.lightboxImage}
            src={lightbox.src}
            alt={lightbox.alt || ''}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </>
  );
}
